/**
 * @fileoverview Hook for product-only performance data (no country breakdown).
 * @module shared/hooks/dashboard/useProductPerformance
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'
import type { ProductPerformance } from './useCountryProductPerformance'

/**
 * Hook result for product performance data.
 */
export interface UseProductPerformanceResult {
  /** Array of product performance data */
  products: ProductPerformance[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Hook to get product-only performance data from dashboard data.
 * Returns aggregated product metrics without country breakdown.
 *
 * @returns Product performance data with loading and error states
 * @example
 * ```typescript
 * const { products, isLoading } = useProductPerformance();
 * ```
 */
export function useProductPerformance(): UseProductPerformanceResult {
  const { salesProductOnlyQuery } = useDashboardData()
  const salesPoints = salesProductOnlyQuery.data?.data

  const dimString = (d: Record<string, unknown> | undefined, key: string): string | undefined => {
    if (!d) return undefined
    const v = d[key as keyof typeof d]
    return typeof v === 'string' ? v : undefined
  }

  const products = useMemo((): ProductPerformance[] => {
    if (!salesPoints) {
      return []
    }

    return salesPoints
      .map((point): ProductPerformance => {
        const revenue = point.metrics.sales || 0
        const adSpend = point.metrics.advertising || 0
        const cogs = point.metrics.cogs || 0
        // directCosts are excluded from this table's Total Expenses by design
        const totalExpenses = adSpend + cogs
        const cm2 =
          typeof point.metrics.cm2 === 'number' ? point.metrics.cm2 : revenue - totalExpenses

        return {
          sku: dimString(point.dimensions as Record<string, unknown>, 'product') || '',
          quantity: point.metrics.quantity || 0,
          sales: revenue,
          adSpend: adSpend,
          adPlatformBreakdown: point.metrics.platformBreakdown || {},
          cogs: cogs,
          totalExpenses: totalExpenses,
          profit: cm2,
          roas: revenue > 0 && adSpend > 0 ? revenue / adSpend : 0,
          margin: revenue > 0 ? cm2 / revenue : 0,
        }
      })
      // Sort by sales descending for stable UX
      .sort((a, b) => b.sales - a.sales)
  }, [salesPoints])

  return {
    products,
    isLoading: salesProductOnlyQuery.isLoading,
    isError: salesProductOnlyQuery.isError,
    error: salesProductOnlyQuery.error?.message || null,
  }
}
